/**
 * GoldenMeadHallInterior - Norse tavern/mead hall interior
 * 
 * Features:
 * - Timber walls and floor
 * - Long tables with benches
 * - Central hearth with fire
 * - Bar counter area
 * - Barrels and decorations
 * - Warm ambient lighting
 */

import { useRef, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import * as THREE from 'three';
import { Barrel, Crate } from './NorseBuildings';

// Interior colors
const COLORS = {
  floorWood: '#5D4037',
  wallWood: '#6D4C41',
  darkWood: '#3E2723',
  tableWood: '#4E342E',
  metalAccent: '#8D8D8D',
  fireGlow: '#FF6B35',
  warmLight: '#FFB366',
};

// Room dimensions
const ROOM = {
  width: 14,
  depth: 18,
  height: 5,
  wallThickness: 0.4,
};

/**
 * Long table with benches
 */
function LongTable({ position, rotation = [0, 0, 0], length = 4 }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Table collision */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[length/2, 0.4, 0.5]} position={[0, 0.4, 0]} />
      </RigidBody>
      
      {/* Table top */}
      <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[length, 0.1, 0.9]} />
        <meshStandardMaterial color={COLORS.tableWood} roughness={0.8} />
      </mesh>
      
      {/* Table legs */}
      {[[-length/2 + 0.2, 0], [length/2 - 0.2, 0], [-length/2 + 0.2, 0.3], [length/2 - 0.2, 0.3], [-length/2 + 0.2, -0.3], [length/2 - 0.2, -0.3]].map(([x, z], i) => (
        <mesh key={`leg-${i}`} position={[x, 0.35, z]} castShadow>
          <boxGeometry args={[0.12, 0.7, 0.12]} />
          <meshStandardMaterial color={COLORS.darkWood} roughness={0.85} />
        </mesh>
      ))}
      
      {/* Benches */}
      {[-0.8, 0.8].map((zOffset, i) => (
        <group key={`bench-${i}`} position={[0, 0, zOffset]}>
          {/* Bench seat */}
          <mesh position={[0, 0.4, 0]} castShadow>
            <boxGeometry args={[length - 0.3, 0.08, 0.35]} />
            <meshStandardMaterial color={COLORS.tableWood} roughness={0.85} />
          </mesh>
          {/* Bench legs */}
          {[-length/2 + 0.3, 0, length/2 - 0.3].map((x, j) => (
            <mesh key={`bleg-${j}`} position={[x, 0.2, 0]} castShadow>
              <boxGeometry args={[0.08, 0.4, 0.3]} />
              <meshStandardMaterial color={COLORS.darkWood} roughness={0.85} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

/**
 * Central hearth with animated fire
 */
function Hearth({ position }) {
  const fireRef = useRef();
  const lightRef = useRef();
  
  useFrame((state) => {
    const time = state.clock.elapsedTime;
    const flicker = Math.sin(time * 10) * 0.15 + Math.sin(time * 7) * 0.1 + 0.75;
    
    if (lightRef.current) {
      lightRef.current.intensity = 3 * flicker;
    }
    
    if (fireRef.current) {
      fireRef.current.scale.y = 0.8 + Math.sin(time * 8) * 0.2;
    }
  });
  
  return (
    <group position={position}>
      {/* Stone hearth base */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[1.2, 0.25, 1.2]} position={[0, 0.25, 0]} />
      </RigidBody>
      
      <mesh position={[0, 0.2, 0]} receiveShadow>
        <cylinderGeometry args={[1.2, 1.4, 0.4, 8]} />
        <meshStandardMaterial color="#5A5A5A" roughness={0.95} />
      </mesh>
      
      {/* Inner pit */}
      <mesh position={[0, 0.25, 0]}>
        <cylinderGeometry args={[0.9, 0.9, 0.15, 8]} />
        <meshStandardMaterial color="#2A2A2A" roughness={1} />
      </mesh>
      
      {/* Logs */}
      {[0, 0.5, 1, 1.5, 2].map((rot, i) => (
        <mesh key={`log-${i}`} position={[Math.cos(rot) * 0.4, 0.4, Math.sin(rot) * 0.4]} rotation={[0.1, rot, 0.2]} castShadow>
          <cylinderGeometry args={[0.08, 0.1, 0.7, 6]} />
          <meshStandardMaterial color="#3D2914" roughness={0.9} />
        </mesh>
      ))}
      
      {/* Fire */}
      <group ref={fireRef} position={[0, 0.5, 0]}>
        {/* Core flame */}
        <mesh>
          <coneGeometry args={[0.3, 0.8, 8]} />
          <meshStandardMaterial 
            color="#FF4500" 
            emissive="#FF2200" 
            emissiveIntensity={2}
            transparent
            opacity={0.9}
          />
        </mesh>
        {/* Outer flame */}
        <mesh position={[0.15, -0.1, 0.1]}>
          <coneGeometry args={[0.2, 0.5, 6]} />
          <meshStandardMaterial 
            color="#FF6600" 
            emissive="#FF4400" 
            emissiveIntensity={1.5}
            transparent
            opacity={0.7}
          />
        </mesh>
        <mesh position={[-0.15, -0.1, -0.1]}>
          <coneGeometry args={[0.18, 0.45, 6]} />
          <meshStandardMaterial 
            color="#FFaa00" 
            emissive="#FF6600" 
            emissiveIntensity={1.5}
            transparent
            opacity={0.7}
          />
        </mesh>
      </group>
      
      {/* Fire light */}
      <pointLight 
        ref={lightRef}
        position={[0, 1, 0]} 
        color="#FF8844" 
        intensity={3} 
        distance={15}
        castShadow
        shadow-mapSize={[512, 512]}
      />
    </group>
  );
}

/**
 * Bar counter
 */
function BarCounter({ position }) {
  return (
    <group position={position}>
      {/* Counter collision */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[2.5, 0.6, 0.6]} position={[0, 0.6, 0]} />
      </RigidBody>
      
      {/* Counter top */}
      <mesh position={[0, 1.1, 0]} castShadow receiveShadow>
        <boxGeometry args={[5, 0.15, 1.2]} />
        <meshStandardMaterial color={COLORS.tableWood} roughness={0.75} />
      </mesh>
      
      {/* Counter front */}
      <mesh position={[0, 0.5, 0.5]} castShadow>
        <boxGeometry args={[5, 1, 0.15]} />
        <meshStandardMaterial color={COLORS.darkWood} roughness={0.8} />
      </mesh>
      
      {/* Counter base */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <boxGeometry args={[5, 1, 1]} />
        <meshStandardMaterial color={COLORS.darkWood} roughness={0.85} />
      </mesh>
      
      {/* Shelves behind (decorative) */}
      <mesh position={[0, 1.8, -0.8]} castShadow>
        <boxGeometry args={[4.5, 0.1, 0.4]} />
        <meshStandardMaterial color={COLORS.tableWood} roughness={0.8} />
      </mesh>
      <mesh position={[0, 2.4, -0.8]} castShadow>
        <boxGeometry args={[4.5, 0.1, 0.4]} />
        <meshStandardMaterial color={COLORS.tableWood} roughness={0.8} />
      </mesh>
      
      {/* Mugs on counter */}
      {[-1.5, -0.5, 0.8, 1.8].map((x, i) => (
        <mesh key={`mug-${i}`} position={[x, 1.25, 0.2]} castShadow>
          <cylinderGeometry args={[0.06, 0.05, 0.15, 8]} />
          <meshStandardMaterial color="#8D8D8D" metalness={0.4} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Wall mounted shield decoration
 */
function WallShield({ position, rotation = [0, 0, 0] }) {
  return (
    <group position={position} rotation={rotation}>
      <mesh castShadow>
        <cylinderGeometry args={[0.4, 0.4, 0.08, 16]} />
        <meshStandardMaterial color="#8B4513" roughness={0.7} />
      </mesh>
      {/* Metal rim */}
      <mesh position={[0, 0, 0.02]}>
        <torusGeometry args={[0.38, 0.03, 8, 24]} />
        <meshStandardMaterial color="#7B7B7B" metalness={0.7} roughness={0.4} />
      </mesh>
      {/* Center boss */}
      <mesh position={[0, 0, 0.05]}>
        <sphereGeometry args={[0.1, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#7B7B7B" metalness={0.7} roughness={0.4} />
      </mesh>
    </group>
  );
}

/**
 * Bar stool seat
 */
function BarStool({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Seat */}
      <mesh position={[0, 0.65, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.2, 0.06, 12]} />
        <meshStandardMaterial color={COLORS.tableWood} roughness={0.75} />
      </mesh>
      {/* Leg */}
      <mesh position={[0, 0.32, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.06, 0.65, 8]} />
        <meshStandardMaterial color={COLORS.metalAccent} roughness={0.4} metalness={0.6} />
      </mesh>
      {/* Footrest ring */}
      <mesh position={[0, 0.2, 0]}>
        <torusGeometry args={[0.12, 0.02, 8, 16]} />
        <meshStandardMaterial color={COLORS.metalAccent} roughness={0.4} metalness={0.6} />
      </mesh>
    </group>
  );
}

/**
 * Hearth bench seat
 */
function HearthBench({ position, rotation = 0, length = 2 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Bench collision */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[length/2, 0.25, 0.3]} position={[0, 0.25, 0]} />
      </RigidBody>
      {/* Seat */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[length, 0.1, 0.5]} />
        <meshStandardMaterial color={COLORS.tableWood} roughness={0.8} />
      </mesh>
      {/* Legs */}
      {[-length/2 + 0.15, length/2 - 0.15].map((x, i) => (
        <mesh key={`leg-${i}`} position={[x, 0.18, 0]} castShadow>
          <boxGeometry args={[0.1, 0.35, 0.4]} />
          <meshStandardMaterial color={COLORS.darkWood} roughness={0.85} />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Interior Layout Data
 */
const INTERIOR_LAYOUT = {
  tables: [
    { position: [-3.5, 0, -2], rotation: [0, 0, 0], length: 5 },
    { position: [3.5, 0, -2], rotation: [0, 0, 0], length: 5 },
    { position: [-3, 0, 3], rotation: [0, 0, 0], length: 4 },
    { position: [3, 0, 3], rotation: [0, 0, 0], length: 4 },
  ],
  
  // Bar stools in front of counter (social zone: bar)
  barStools: [
    { position: [-1.8, 0, -6.5], rotation: 0 },
    { position: [-0.9, 0, -6.5], rotation: 0 },
    { position: [0, 0, -6.5], rotation: 0 },
    { position: [0.9, 0, -6.5], rotation: 0 },
    { position: [1.8, 0, -6.5], rotation: 0 },
  ],
  
  // Hearth benches around fire (social zone: hearth)
  hearthBenches: [
    { position: [-1.8, 0, -5], rotation: Math.PI/2, length: 1.5 },
    { position: [1.8, 0, -5], rotation: -Math.PI/2, length: 1.5 },
    { position: [0, 0, -3.5], rotation: Math.PI, length: 2 },
    { position: [0, 0, -6.5], rotation: 0, length: 2 },
  ],
  
  barrels: [
    { position: [-6, 0, -7], rotation: [0, 0.3, 0] },
    { position: [-5.5, 0, -7.5], rotation: [0, -0.2, 0] },
    { position: [-5, 0, -7], rotation: [0, 0.5, 0] },
    { position: [6, 0, -6], rotation: [0, 0.1, 0] },
    { position: [5.5, 0, -6.5], rotation: [0, -0.3, 0] },
    { position: [6.2, 0, -7.8], rotation: [0, 0.6, 0] },
  ],
  
  crates: [
    { position: [6, 0, -7.5], rotation: [0, 0.2, 0] },
    { position: [-6.5, 0, -6.5], rotation: [0, -0.1, 0] },
    { position: [6.3, 0, -5.5], rotation: [0, 0.4, 0] },
  ],
  
  shields: [
    { position: [-6.8, 2.5, 0], rotation: [0, Math.PI/2, 0] },
    { position: [-6.8, 2.5, -4], rotation: [0, Math.PI/2, 0] },
    { position: [6.8, 2.5, 0], rotation: [0, -Math.PI/2, 0] },
    { position: [6.8, 2.5, -4], rotation: [0, -Math.PI/2, 0] },
  ],
  
  exitZone: {
    position: [0, 0, 8.5],
    radius: 2.5,
  },
};

/**
 * Wall Torch with visible flame - EMISSIVE ONLY (no realtime light for performance)
 * The visual glow comes from emissive materials, not expensive PointLights
 */
function WallTorch({ position, rotation = [0, 0, 0] }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Torch holder (metal bracket) */}
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[0.15, 0.08, 0.12]} />
        <meshStandardMaterial color="#4A4A4A" metalness={0.6} roughness={0.4} />
      </mesh>
      
      {/* Torch handle */}
      <mesh position={[0, -0.3, 0.1]} rotation={[0.3, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.04, 0.4, 8]} />
        <meshStandardMaterial color="#3E2723" roughness={0.9} />
      </mesh>
      
      {/* Visible flame (emissive mesh - creates visual glow without real light) */}
      <mesh position={[0, -0.05, 0.15]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.08, 0.2, 6]} />
        <meshBasicMaterial 
          color="#FF6622"
          transparent
          opacity={0.9}
        />
      </mesh>
      
      {/* Inner flame core (brighter emissive) */}
      <mesh position={[0, 0, 0.15]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.04, 0.15, 5]} />
        <meshBasicMaterial 
          color="#FFCC00"
          transparent
          opacity={0.95}
        />
      </mesh>
      
    </group>
  );
}

/**
 * Hanging oil lamp - EMISSIVE ONLY (no realtime light for performance)
 */
function HangingLamp({ position }) {
  return (
    <group position={position}>
      {/* Chain */}
      <mesh position={[0, 0.3, 0]}>
        <cylinderGeometry args={[0.01, 0.01, 0.6, 4]} />
        <meshStandardMaterial color="#555555" metalness={0.8} roughness={0.3} />
      </mesh>
      
      {/* Lamp body */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.12, 0.08, 0.15, 8]} />
        <meshStandardMaterial color="#6D4C41" metalness={0.2} roughness={0.7} />
      </mesh>
      
      {/* Lamp glass (emissive glow - no real light) */}
      <mesh position={[0, 0.1, 0]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshBasicMaterial 
          color="#FFBB66"
        />
      </mesh>
    </group>
  );
}

/**
 * Main Golden Mead Hall Interior Component
 */
export default function GoldenMeadHallInterior() {
  const { scene } = useThree();
  const layout = INTERIOR_LAYOUT;
  
  useEffect(() => {
    scene.background = new THREE.Color('#1A0F08');
    scene.fog = null;
  }, [scene]);
  
  return (
    <group>
      {/* Ambient lighting - warm Norse tavern feel */}
      <ambientLight intensity={0.4} color="#FFF0DD" />
      
      {/* Main overhead fill light (simulates light from smoke hole/skylights) */}
      <directionalLight 
        position={[0, 8, -5]} 
        intensity={0.5} 
        color="#FFE8D6"
      />
      
      {/* Secondary fill from doorway */}
      <directionalLight 
        position={[0, 3, 10]} 
        intensity={0.3} 
        color="#88AACC"
      />
      
      {/* Wall torches - left wall */}
      <WallTorch position={[-6.6, 2.2, 4]} rotation={[0, Math.PI/2, 0]} />
      <WallTorch position={[-6.6, 2.2, 0]} rotation={[0, Math.PI/2, 0]} />
      <WallTorch position={[-6.6, 2.2, -4]} rotation={[0, Math.PI/2, 0]} />
      
      {/* Wall torches - right wall */}
      <WallTorch position={[6.6, 2.2, 4]} rotation={[0, -Math.PI/2, 0]} />
      <WallTorch position={[6.6, 2.2, 0]} rotation={[0, -Math.PI/2, 0]} />
      <WallTorch position={[6.6, 2.2, -4]} rotation={[0, -Math.PI/2, 0]} />
      
      {/* Wall torches - back wall (behind bar) */}
      <WallTorch position={[-3, 2.2, -8.6]} rotation={[0, 0, 0]} />
      <WallTorch position={[3, 2.2, -8.6]} rotation={[0, 0, 0]} />
      
      {/* Hanging lamps over tables */}
      <HangingLamp position={[-3.5, 3.5, -2]} />
      <HangingLamp position={[3.5, 3.5, -2]} />
      <HangingLamp position={[0, 3.5, 2]} />
      
      {/* Floor */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[ROOM.width/2, 0.1, ROOM.depth/2]} position={[0, -0.1, 0]} />
      </RigidBody>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} receiveShadow>
        <planeGeometry args={[ROOM.width, ROOM.depth]} />
        <meshStandardMaterial color={COLORS.floorWood} roughness={0.85} />
      </mesh>
      
      {/* Floor planks (visual detail) */}
      {Array.from({ length: 14 }).map((_, i) => (
        <mesh key={`plank-${i}`} rotation={[-Math.PI / 2, 0, 0]} position={[-6.5 + i, 0.015, 0]} receiveShadow>
          <planeGeometry args={[0.95, ROOM.depth - 0.2]} />
          <meshStandardMaterial color={i % 2 === 0 ? COLORS.floorWood : '#5A4535'} roughness={0.9} />
        </mesh>
      ))}
      
      {/* Ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, ROOM.height, 0]}>
        <planeGeometry args={[ROOM.width, ROOM.depth]} />
        <meshStandardMaterial color="#2D1F14" roughness={0.9} />
      </mesh>
      
      {/* Ceiling beams REMOVED - they were blocking camera view inside the Mead Hall */}
      
      {/* Back wall */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[ROOM.width/2, ROOM.height/2, ROOM.wallThickness/2]} position={[0, ROOM.height/2, -ROOM.depth/2]} />
      </RigidBody>
      <mesh position={[0, ROOM.height/2, -ROOM.depth/2]} receiveShadow>
        <boxGeometry args={[ROOM.width, ROOM.height, ROOM.wallThickness]} />
        <meshStandardMaterial color={COLORS.wallWood} roughness={0.85} />
      </mesh>
      
      {/* Front wall - left section */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[ROOM.width/4 - 1, ROOM.height/2, ROOM.wallThickness/2]} position={[-ROOM.width/4 - 1.5, ROOM.height/2, ROOM.depth/2]} />
      </RigidBody>
      <mesh position={[-ROOM.width/4 - 1.5, ROOM.height/2, ROOM.depth/2]} receiveShadow>
        <boxGeometry args={[ROOM.width/2 - 2, ROOM.height, ROOM.wallThickness]} />
        <meshStandardMaterial color={COLORS.wallWood} roughness={0.85} />
      </mesh>
      
      {/* Front wall - right section */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[ROOM.width/4 - 1, ROOM.height/2, ROOM.wallThickness/2]} position={[ROOM.width/4 + 1.5, ROOM.height/2, ROOM.depth/2]} />
      </RigidBody>
      <mesh position={[ROOM.width/4 + 1.5, ROOM.height/2, ROOM.depth/2]} receiveShadow>
        <boxGeometry args={[ROOM.width/2 - 2, ROOM.height, ROOM.wallThickness]} />
        <meshStandardMaterial color={COLORS.wallWood} roughness={0.85} />
      </mesh>
      
      {/* Front wall - above door */}
      <mesh position={[0, ROOM.height - 1, ROOM.depth/2]} receiveShadow>
        <boxGeometry args={[3, 2, ROOM.wallThickness]} />
        <meshStandardMaterial color={COLORS.wallWood} roughness={0.85} />
      </mesh>
      
      {/* Side walls */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[ROOM.wallThickness/2, ROOM.height/2, ROOM.depth/2]} position={[-ROOM.width/2, ROOM.height/2, 0]} />
      </RigidBody>
      <mesh position={[-ROOM.width/2, ROOM.height/2, 0]} receiveShadow>
        <boxGeometry args={[ROOM.wallThickness, ROOM.height, ROOM.depth]} />
        <meshStandardMaterial color={COLORS.wallWood} roughness={0.85} />
      </mesh>
      
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[ROOM.wallThickness/2, ROOM.height/2, ROOM.depth/2]} position={[ROOM.width/2, ROOM.height/2, 0]} />
      </RigidBody>
      <mesh position={[ROOM.width/2, ROOM.height/2, 0]} receiveShadow>
        <boxGeometry args={[ROOM.wallThickness, ROOM.height, ROOM.depth]} />
        <meshStandardMaterial color={COLORS.wallWood} roughness={0.85} />
      </mesh>
      
      {/* Wall timber details */}
      {[-6.7, 6.7].map((x, wallIdx) => (
        [1, 2, 3, 4].map((h, i) => (
          <mesh key={`wbeam-${wallIdx}-${i}`} position={[x, h, 0]} castShadow>
            <boxGeometry args={[0.12, 0.1, ROOM.depth - 0.5]} />
            <meshStandardMaterial color={COLORS.darkWood} roughness={0.8} />
          </mesh>
        ))
      ))}
      
      {/* Central hearth */}
      <Hearth position={[0, 0, -5]} />
      
      {/* Hearth benches (social zone: hearth) */}
      {layout.hearthBenches.map((bench, i) => (
        <HearthBench key={`hbench-${i}`} {...bench} />
      ))}
      
      {/* Bar counter */}
      <BarCounter position={[0, 0, -7.5]} />
      
      {/* Bar stools (social zone: bar) */}
      {layout.barStools.map((stool, i) => (
        <BarStool key={`stool-${i}`} {...stool} />
      ))}
      
      {/* Tables */}
      {layout.tables.map((table, i) => (
        <LongTable key={`table-${i}`} {...table} />
      ))}
      
      {/* Barrels */}
      {layout.barrels.map((barrel, i) => (
        <Barrel key={`barrel-${i}`} {...barrel} />
      ))}
      
      {/* Crates */}
      {layout.crates.map((crate, i) => (
        <Crate key={`crate-${i}`} {...crate} />
      ))}
      
      {/* Wall shields */}
      {layout.shields.map((shield, i) => (
        <WallShield key={`shield-${i}`} {...shield} />
      ))}
      
      {/* Exit zone - invisible (ring geometry removed to prevent circular artifacts) */}
      
      {/* Door frame */}
      <mesh position={[0, 2, ROOM.depth/2 + 0.05]} castShadow>
        <boxGeometry args={[2.5, 4, 0.15]} />
        <meshStandardMaterial color={COLORS.darkWood} roughness={0.75} />
      </mesh>
      
      {/* Light from outside (subtle) */}
      <pointLight position={[0, 2, ROOM.depth/2 + 1]} color="#AABBCC" intensity={0.5} distance={5} />
    </group>
  );
}
